(ns practitest.frontend.ptql-editor
  (:require
    ["/codemirror/ptql-mode" :refer [ptqlLanguage]]
    ["@catppuccin/codemirror" :refer [catppuccinMocha]]
    ["@codemirror/autocomplete" :refer [autocompletion moveCompletionSelection
                                        startCompletion]]
    ["@codemirror/commands" :refer [defaultKeymap]]
    ["@codemirror/state" :refer [Compartment EditorState Prec]]
    ["@codemirror/view" :refer [EditorView keymap]]
    [clojure.string :as str]
    [practitest.frontend.ptql-editor.autocomplete :as autocomplete]
    [practitest.frontend.ptql.parser :as ptql-parser]
    [reagent.core :as r]))

(defn- compute-parse-error [value]
  (when-not (str/blank? value)
    (let [result (ptql-parser/parse value)]
      (when (ptql-parser/failure? result)
        (str "Syntax error at position "
             (ptql-parser/get-failure-index result))))))

(defn- make-extensions [{:keys [fields-by-entity source-entity users groups read-only? on-change on-submit on-parse-result]}]
  (let [read-only-c (Compartment.)]
    {:read-only-c read-only-c
     :extensions
     (cond-> [(.of keymap (clj->js defaultKeymap))
              (.of keymap (clj->js [{:key "Enter"
                                     :run (fn [^js view]
                                            (when on-submit
                                              (on-submit (.toString (.-doc (.-state view)))))
                                            true)}]))
              (.high Prec
                     (.of keymap (clj->js [{:key "Ctrl-n" :run (moveCompletionSelection true)}
                                           {:key "Ctrl-p" :run (moveCompletionSelection false)}])))
              ptqlLanguage
              catppuccinMocha
              (.-lineWrapping EditorView)
              (autocompletion (clj->js {:override [(autocomplete/completion-source
                                                     (or fields-by-entity {})
                                                     source-entity
                                                     users
                                                     groups)]
                                        :activateOnTyping true
                                        :aboveCursor true}))
              (.of read-only-c (.of (.-readOnly EditorState) (boolean read-only?)))
              (.of (.-inputHandler EditorView)
                   (fn [^js view _from _to text]
                     (when (= text " ")
                       (js/setTimeout #(startCompletion view) 0))
                     false))
              (.of (.-updateListener EditorView)
                   (fn [^js update]
                     (when (.-docChanged update)
                       (let [doc (.toString (.-doc (.-state update)))]
                         (when on-change (on-change doc))
                         (when on-parse-result (on-parse-result (compute-parse-error doc)))))))]
       read-only?
       (conj (.of (.-contentAttributes EditorView)
                  (clj->js {:tabindex "-1"}))))}))

(defn editor
  "CM6 PTQL editor component.

  Props:
  - `:value`: PTQL string
  - `:on-change`: `(fn [new-ptql-string])`
  - `:on-submit`: `(fn [ptql-string])` called on Enter/Ctrl+Enter
  - `:on-parse-result`: `(fn [error])` called after each parse with the error string
                        or nil when the input is valid
  - `:fields-by-entity`: map of PTQL entity name to field list, e.g.
    `{\"Issue\" [{:name \"Status\" :type \"list\" :values [\"Open\" ...]} ...]
      \"Test\" [...]
      ...}`
    Used for context-aware autocomplete: when the cursor is inside a `LINKED TO <Entity> WHERE (...)` clause,
    fields are sourced from <Entity>, otherwise from `:source-entity`.
  - `:source-entity`: PTQL entity name (e.g. \"Test\") - the default scope for autocomplete
  - `:users`: vector of user objects with key `:email`
  - `:groups`: vector of group objects with key `:name`
  - `:read-only?`: boolean
  - `:auto-focus?`: boolean - focus the editor on mount"
  [{:keys [value on-parse-result] :as opts}]
  (let [container-ref (atom nil)
        view-ref (atom nil)
        read-only-c-ref (atom nil)]
    (r/create-class
      {:display-name "ptql-editor"

       :component-did-mount
       (fn [_this]
         (let [dom-node @container-ref
               doc (or value "")
               {:keys [read-only-c extensions]} (make-extensions opts)]
           (reset! read-only-c-ref read-only-c)
           (reset! view-ref
                   (EditorView. (clj->js {:state (.create EditorState (clj->js {:doc (or value "")
                                                                                :extensions extensions
                                                                                :selection {:anchor (count doc)}}))
                                          :parent dom-node})))
           (when on-parse-result (on-parse-result (compute-parse-error doc)))
           (when (:auto-focus? opts)
             (.focus ^js @view-ref))))

       :component-did-update
       (fn [this [_ prev-props]]
         (when-let [v ^js @view-ref]
           (let [{:keys [value read-only?]} (r/props this)]
             ;; sync value if changed externally
             (when (and value (not= value (.toString (.-doc (.-state v)))))
               (.dispatch v
                          (clj->js {:changes {:from 0
                                              :to (.. v -state -doc -length)
                                              :insert value}})))
             ;; sync read-only
             (when (not= read-only? (:read-only? prev-props))
               (.dispatch v
                          (clj->js {:effects (.reconfigure @read-only-c-ref
                                                           (.of (.-readOnly EditorState) (boolean read-only?)))}))))))

       :component-will-unmount
       (fn [_]
         (when-let [v @view-ref]
           (.destroy v)
           (reset! view-ref nil)))

       :reagent-render
       (fn [_]
         [:div
          [:div.ptql-editor-container {:ref #(reset! container-ref %)}]])})))
